import {
    startTransition,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    useSyncExternalStore,
} from 'react';

import { useIsomorphicLayoutEffect } from '@/hooks/utils/useIsomorphicLayoutEffect';
import { SsrViewportWidthContext } from '@/providers/SsrViewportWidthContext';
import { BREAKPOINT_CONSTANTS, BREAKPOINT_SIZES } from '@/utils/constants/ui';
import getBreakpointInfoFromViewportWidth from '@/utils/ui/getBreakpointInfoFromViewportWidth';
import getViewportWidthFromDocumentCookie from '@/utils/ui/getViewportWidthFromDocumentCookie';

export interface Breakpoints {
    isMobilePortrait: boolean;
    isMobileLandscape: boolean;
    isTabletPortrait: boolean;
    isTabletLandscape: boolean;
    isDesktop: boolean;
    isDesktopWide: boolean;
    isMobile: boolean;
    isTabletPortraitOrWider: boolean;
    isTabletLandscapeOrWider: boolean;
    active:
        | BREAKPOINT_CONSTANTS.MOBILE_PORTRAIT
        | BREAKPOINT_CONSTANTS.MOBILE_LANDSCAPE
        | BREAKPOINT_CONSTANTS.TABLET_PORTRAIT
        | BREAKPOINT_CONSTANTS.TABLET_LANDSCAPE
        | BREAKPOINT_CONSTANTS.DESKTOP
        | BREAKPOINT_CONSTANTS.DESKTOP_WIDE;
}

/** Matches former `useMedia` defaults when no SSR width; used only for `getServerSnapshot`. */
const DEFAULT_SSR_BREAKPOINTS: Breakpoints = {
    isMobilePortrait: false,
    isMobileLandscape: false,
    isTabletPortrait: false,
    isTabletLandscape: false,
    isTabletPortraitOrWider: true,
    isTabletLandscapeOrWider: true,
    isDesktop: true,
    isDesktopWide: false,
    isMobile: false,
    active: BREAKPOINT_CONSTANTS.DESKTOP,
};

const QUERY_MOBILE_PORTRAIT = `(max-width: ${BREAKPOINT_SIZES.MOBILE_PORTRAIT_MAX})`;
const QUERY_MOBILE_LANDSCAPE = `(min-width: ${BREAKPOINT_SIZES.MOBILE_LANDSCAPE_MIN}) and (max-width: ${BREAKPOINT_SIZES.MOBILE_LANDSCAPE_MAX})`;
const QUERY_TABLET_PORTRAIT = `(min-width: ${BREAKPOINT_SIZES.TABLET_PORTRAIT_MIN}) and (max-width: ${BREAKPOINT_SIZES.TABLET_PORTRAIT_MAX})`;
const QUERY_TABLET_LANDSCAPE = `(min-width: ${BREAKPOINT_SIZES.TABLET_LANDSCAPE_MIN}) and (max-width: ${BREAKPOINT_SIZES.TABLET_LANDSCAPE_MAX})`;
const QUERY_DESKTOP = `(min-width: ${BREAKPOINT_SIZES.DESKTOP_MIN}) and (max-width: ${BREAKPOINT_SIZES.DESKTOP_MAX})`;
const QUERY_DESKTOP_WIDE = `(min-width: ${BREAKPOINT_SIZES.DESKTOP_WIDE_MIN})`;
const QUERY_TABLET_PORTRAIT_OR_WIDER = `(min-width: ${BREAKPOINT_SIZES.TABLET_PORTRAIT_MIN})`;
const QUERY_TABLET_LANDSCAPE_OR_WIDER = `(min-width: ${BREAKPOINT_SIZES.TABLET_LANDSCAPE_MIN})`;

const MEDIA_QUERIES = [
    QUERY_MOBILE_PORTRAIT,
    QUERY_MOBILE_LANDSCAPE,
    QUERY_TABLET_PORTRAIT,
    QUERY_TABLET_LANDSCAPE,
    QUERY_DESKTOP,
    QUERY_DESKTOP_WIDE,
    QUERY_TABLET_PORTRAIT_OR_WIDER,
    QUERY_TABLET_LANDSCAPE_OR_WIDER,
] as const;

type MediaMatchFlags = Pick<
    Breakpoints,
    | 'isMobilePortrait'
    | 'isMobileLandscape'
    | 'isTabletPortrait'
    | 'isTabletLandscape'
    | 'isDesktop'
    | 'isDesktopWide'
    | 'isTabletPortraitOrWider'
    | 'isTabletLandscapeOrWider'
>;

const breakpointsFromMediaMatches = (m: MediaMatchFlags): Breakpoints => {
    const {
        isMobilePortrait,
        isMobileLandscape,
        isTabletPortrait,
        isTabletLandscape,
        isDesktop,
        isDesktopWide,
        isTabletPortraitOrWider,
        isTabletLandscapeOrWider,
    } = m;

    const isMobile = isMobileLandscape || isMobilePortrait || isTabletLandscape || isTabletPortrait;

    let active: Breakpoints['active'] = BREAKPOINT_CONSTANTS.DESKTOP;
    if (isMobilePortrait) {
        active = BREAKPOINT_CONSTANTS.MOBILE_PORTRAIT;
    } else if (isMobileLandscape) {
        active = BREAKPOINT_CONSTANTS.MOBILE_LANDSCAPE;
    } else if (isTabletPortrait) {
        active = BREAKPOINT_CONSTANTS.TABLET_PORTRAIT;
    } else if (isTabletLandscape) {
        active = BREAKPOINT_CONSTANTS.TABLET_LANDSCAPE;
    } else if (isDesktop) {
        active = BREAKPOINT_CONSTANTS.DESKTOP;
    } else if (isDesktopWide) {
        active = BREAKPOINT_CONSTANTS.DESKTOP_WIDE;
    }

    return {
        isMobilePortrait,
        isMobileLandscape,
        isTabletPortrait,
        isTabletLandscape,
        isDesktop,
        isDesktopWide,
        isMobile,
        isTabletPortraitOrWider,
        isTabletLandscapeOrWider,
        active,
    };
};

const readBreakpointsFromWindow = (): Breakpoints => {
    const isMobilePortrait = window.matchMedia(QUERY_MOBILE_PORTRAIT).matches;
    const isMobileLandscape = window.matchMedia(QUERY_MOBILE_LANDSCAPE).matches;
    const isTabletPortrait = window.matchMedia(QUERY_TABLET_PORTRAIT).matches;
    const isTabletLandscape = window.matchMedia(QUERY_TABLET_LANDSCAPE).matches;
    const isDesktop = window.matchMedia(QUERY_DESKTOP).matches;
    const isDesktopWide = window.matchMedia(QUERY_DESKTOP_WIDE).matches;
    const isTabletPortraitOrWider = window.matchMedia(QUERY_TABLET_PORTRAIT_OR_WIDER).matches;
    const isTabletLandscapeOrWider = window.matchMedia(QUERY_TABLET_LANDSCAPE_OR_WIDER).matches;

    return breakpointsFromMediaMatches({
        isMobilePortrait,
        isMobileLandscape,
        isTabletPortrait,
        isTabletLandscape,
        isDesktop,
        isDesktopWide,
        isTabletPortraitOrWider,
        isTabletLandscapeOrWider,
    });
};

const snapshotSignature = (b: Breakpoints) =>
    `${b.isMobilePortrait}${b.isMobileLandscape}${b.isTabletPortrait}${b.isTabletLandscape}${b.isDesktop}${b.isDesktopWide}${b.isTabletPortraitOrWider}${b.isTabletLandscapeOrWider}${b.active}`;

const subscribeToBreakpointMedia = (onChange: () => void) => {
    const mqls = MEDIA_QUERIES.map((q) => window.matchMedia(q));
    mqls.forEach((mql) => {
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', onChange);
        } else {
            mql.addListener(onChange);
        }
    });
    return () =>
        mqls.forEach((mql) => {
            if (typeof mql.removeEventListener === 'function') {
                mql.removeEventListener('change', onChange);
            } else {
                mql.removeListener(onChange);
            }
        });
};

// Until the first client mount, getSnapshot returns the SSR value so a matchMedia mismatch
// can't tear a dehydrating Suspense boundary (React #421). Module-scoped so late mounts read live.
let appHasHydrated = false;

// Test-only: the flag is module-scoped and never reset in the app, so suites that mount the hook
// leak `appHasHydrated = true` into later tests. Resetting per test keeps the SSR-gate case hermetic.
const resetAppHasHydratedForTests = () => {
    appHasHydrated = false;
};

const useBreakpoints = (initialViewportWidth?: number | null): Breakpoints => {
    const ssrViewportWidthFromContext = useContext(SsrViewportWidthContext);

    const finiteSsrViewportWidth = useMemo((): number | undefined => {
        if (initialViewportWidth != null && Number.isFinite(initialViewportWidth)) {
            return Math.round(Number(initialViewportWidth));
        }
        if (
            ssrViewportWidthFromContext !== undefined &&
            ssrViewportWidthFromContext != null &&
            Number.isFinite(ssrViewportWidthFromContext)
        ) {
            return Math.round(Number(ssrViewportWidthFromContext));
        }
        return undefined;
    }, [initialViewportWidth, ssrViewportWidthFromContext]);

    const [documentCookieWidth, setDocumentCookieWidth] = useState<number | undefined>(undefined);

    useIsomorphicLayoutEffect(() => {
        if (finiteSsrViewportWidth != null) {
            return;
        }
        const w = getViewportWidthFromDocumentCookie();
        if (w != null) {
            setDocumentCookieWidth(w);
        }
    }, [finiteSsrViewportWidth]);

    const resolvedViewportWidth = finiteSsrViewportWidth ?? documentCookieWidth;

    const serverSnapshot = useMemo((): Breakpoints => {
        if (resolvedViewportWidth == null || !Number.isFinite(resolvedViewportWidth)) {
            return DEFAULT_SSR_BREAKPOINTS;
        }
        return getBreakpointInfoFromViewportWidth(resolvedViewportWidth);
    }, [resolvedViewportWidth]);

    const cacheRef = useRef<{ sig: string; value: Breakpoints } | null>(null);

    // Resync to the live value in a transition after mount so hydration finishes first.
    const [, resyncAfterHydration] = useState(0);
    useEffect(() => {
        appHasHydrated = true;
        startTransition(() => resyncAfterHydration((n) => n + 1));
    }, []);

    const getSnapshot = useCallback(() => {
        if (!appHasHydrated) {
            return serverSnapshot;
        }
        const next = readBreakpointsFromWindow();
        const sig = snapshotSignature(next);
        if (sig === snapshotSignature(serverSnapshot)) {
            return serverSnapshot;
        }
        const prev = cacheRef.current;
        if (prev && prev.sig === sig) {
            return prev.value;
        }
        cacheRef.current = { sig, value: next };
        return next;
    }, [serverSnapshot]);

    const getServerSnapshot = useCallback(() => serverSnapshot, [serverSnapshot]);

    return useSyncExternalStore(subscribeToBreakpointMedia, getSnapshot, getServerSnapshot);
};

const isBreakpointInfoSyncedWithWindow = (breakpointInfo: Breakpoints): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    return snapshotSignature(breakpointInfo) === snapshotSignature(readBreakpointsFromWindow());
};

export { DEFAULT_SSR_BREAKPOINTS, isBreakpointInfoSyncedWithWindow, resetAppHasHydratedForTests };
export default useBreakpoints;
